import { useEffect, useRef, useState } from 'react';
import { SketchPicker } from 'react-color';
import Color from 'color';
import { Overlay } from 'react-bootstrap';
import TextInput from './TextInput';
import Button from './Button';
import PropTypes from 'prop-types';
import Icon from './Icon';
import { withComponentCapabilities } from './capabilities/componentCapabilities';

export const customCtrlDef = {
  def: (t, tools) => ({
      form: [{
          borderless: true,
          rows: [
              ...tools.labelFields(t),
              ...tools.fieldFields(t),
              ...tools.commonFields(t)
          ]
      }]
  }),
  control: ({ def, href, ...props }) => <ColorPicker href={href ?? def.href} {...props}></ColorPicker>
}

/**
 * A textbox for handling hex colours with a color picker control
 * @component
 */
const ColorPicker = withComponentCapabilities(({ value, placeholder, required = false, disabled = false, hideLabel = false, href, onChange, style, ...props }) => {
  const [color, setColor] = useState(value);
  const [showPicker, setShowPicker] = useState(false);
  const btnRef = useRef();

  useEffect(() => {
    if (value) {
      try {
        setColor(Color(value).hex().toLowerCase());
      } catch {
        setColor(value);
      }
    } else {
      setColor(value);
    }
  }, [value])

  const handleColorChange = (color) => {
    setColor(color.hex);
    if (color.hex !== value) {
      onChange(color.hex);
    }
  };

  const handleInputChange = (newColor) => {
    if (newColor.match(/^#?[0-9a-fA-F]{6}$/)) {
      handleColorChange({ hex: Color(newColor.charAt(0) === '#' ? newColor : '#' + newColor).hex().toLowerCase() });
      setShowPicker(false);
    } else if (!newColor && !required) {
      handleColorChange({ hex: null });
      setShowPicker(false);
    } else {
      setColor(newColor);
    }
  };

  let textColor, backgroundColor;
  if (color) {
    try {
      textColor = Color(color).isLight() ? '#000000' : '#ffffff';
      backgroundColor = Color(color).hex();
    } catch {
      textColor = '#000000';
      backgroundColor = '#ffffff'
    }
  }

  return (
    <div style={{ position: 'relative'}}>
    <TextInput value={color} pattern="^#[0-9a-fA-F]{6}$" onChange={handleInputChange} live={true} placeholder={placeholder} required={required} disabled={disabled} hideLabel={hideLabel} href={href}
        appendCtrl={<Button ref={btnRef} variant="outline-dark" disabled={disabled} onClick={() => setShowPicker(!showPicker)} style={{ position: 'relative' }}><Icon variant="solid" kind="palette"></Icon></Button>}
        
        style={{
          ...style,
          backgroundColor: backgroundColor,
          color: textColor,
        }}
      />
      <Overlay target={btnRef.current} show={showPicker} placement="bottom-end" rootClose onHide={() => setShowPicker(false)}>
        {({ ...overlayProps }) => (
          <div {...overlayProps} style={{ ...overlayProps.style, zIndex: 10000 }}>
            <SketchPicker color={color ?? ''} onChangeComplete={handleColorChange} disableAlpha={true} />
          </div>
        )}
      </Overlay>
    </div>
  );
}, {

});


ColorPicker.propTypes = {
  /**
   * The color - should be a hex rgb string
   */
  value: PropTypes.string,

  /**
     * Function to call when the value has been changed
     */
  onChange: PropTypes.func.isRequired,

  /**
   * The label for the control
   */
  placeholder: PropTypes.string.isRequired,

  /**
   * The control must have a value for the from in which it is contained to validate
   */
  required: PropTypes.bool,

  /**
   * Force the input to be read-only
   */
  disabled: PropTypes.bool,

  /**
   * Collapses the floating label used for spacing
   */
  hideLabel: PropTypes.bool,

  /**
 * An option navigation URL to be followed if the label text is clicked
 */
href: PropTypes.string,
}

export default ColorPicker;